import React from "react";
import { Check, RefreshCcw } from "lucide-react";
import BaseModal from "../BaseModal";
import "./ProfileRefreshModal.scss";
import { BarIco, BriefcaseIco, GraduationCapIco, BookOpenIco, User } from "../../components/icons";

const EDUCATION_OPTIONS = {
  2: "High School Diploma or GED",
  5: "Associate's (or other 2-year degree)",
  6: "Bachelor's Degree",
  8: "Master's Degree",
  11: "Doctoral Degree",
};

const GRADE_OPTIONS = {
  MD: "Middle",
  FM: "Freshman",
  SM: "Sophomore",
  JR: "Junior",
  SR: "Senior",
  GD: "Graduated",
  OT: "Other",
};

const clampCertainty = (value) => {
  const certaintyValue = Number(value);

  if (!Number.isFinite(certaintyValue)) {
    return 0;
  }

  return Math.min(Math.max(Math.round(certaintyValue), 0), 10);
};

const getCertaintyLabel = (value) => {
  if (value >= 9) return "Very certain";
  if (value >= 7) return "Mostly certain";
  if (value >= 5) return "Somewhat certain";
  if (value >= 3) return "Exploring";
  if (value >= 1) return "Not certain";
  return "Choose a level";
};

const ProfileRefreshModal = ({
  isOpen,
  variant = "monthly",
  user,
  careers,
  certainty,
  isSaving = false,
  saveError = "",
  onClose,
  onCareersChange,
  onCertaintyChange,
  onConfirm,
  onPrimaryAction,
}) => {
  const certaintyValue = clampCertainty(certainty);
  const isMonthly = variant === "monthly";
  const summaryTitle = isMonthly ? "YOUR CURRENT INFO" : "YOUR CURRENT PROFILE";

  return (
    <BaseModal className="profile-refresh-modal" isOpen={isOpen} onClose={onClose} showClose={false}>
      <div className="profile-refresh-modal__icon">
        {isMonthly ? <Check size={22} strokeWidth={2.25} /> : <RefreshCcw size={22} strokeWidth={2.25} />}
      </div>

      <div className="profile-refresh-modal__header">
        <p className="profile-refresh-modal__welcome">Welcome back!</p>
        <h2 className="profile-refresh-modal__title">
          {isMonthly
            ? "Quick check: is this still accurate?"
            : "A lot can change in 6+ months"}
        </h2>
        <p className="profile-refresh-modal__subtitle">
          {isMonthly
            ? "Update anything that has changed — it only takes a moment."
            : "Here is what we have on file. Does it still reflect where you are headed?"}
        </p>
      </div>

      <div className="profile-refresh-modal__panel">
        <div className="profile-refresh-modal__panel-title">{summaryTitle}</div>

        {isMonthly ? (
          <>
            <div className="profile-refresh-modal__field">
              <span className="profile-refresh-modal__label">
                <BriefcaseIco /> Career interests
              </span>
              <input
                className="profile-refresh-modal__input"
                onChange={(event) => onCareersChange(event.target.value)}
                type="text"
                value={careers}
              />
            </div>

            <div className="profile-refresh-modal__field profile-refresh-modal__field--certainty">
              <div className="profile-refresh-modal__certainty-header">
                <span className="profile-refresh-modal__label">
                  <BarIco /> Career certainty
                </span>
                <span className="profile-refresh-modal__certainty-value">
                  {certaintyValue
                    ? `${certaintyValue}/10 – ${getCertaintyLabel(certaintyValue)}`
                    : "Choose your level"}
                </span>
              </div>

              <div className="profile-refresh-modal__scale">
                {Array.from({ length: 10 }, (_, index) => {
                  const itemValue = index + 1;
                  const isSelected = itemValue === certaintyValue;

                  return (
                    <button
                      aria-label={`Set certainty to ${itemValue}`}
                      className={`profile-refresh-modal__scale-item${isSelected ? " is-selected" : ""}`}
                      key={itemValue}
                      onClick={() => onCertaintyChange(itemValue)}
                      type="button"
                    >
                      {itemValue}
                    </button>
                  );
                })}
              </div>

              <div className="profile-refresh-modal__scale-labels">
                <span>Not certain</span>
                <span>Very certain</span>
              </div>
            </div>
          </>
        ) : (
          <div className="profile-refresh-modal__summary">
            <div className="profile-refresh-modal__summary-row">
              <span className="profile-refresh-modal__summary-label">
                <BriefcaseIco /> Career interests
              </span>
              <strong className="profile-refresh-modal__summary-value">{user?.careers || "-"}</strong>
            </div>
            <div className="profile-refresh-modal__summary-row">
              <span className="profile-refresh-modal__summary-label">
                <BarIco /> Career certainty
              </span>
              <strong className="profile-refresh-modal__summary-value">
                {user?.certainty ? `${user.certainty} / 10` : "-"}
              </strong>
            </div>
            <div className="profile-refresh-modal__summary-row">
              <span className="profile-refresh-modal__summary-label">
                <GraduationCapIco /> Education goal
              </span>
              <strong className="profile-refresh-modal__summary-value">
                {EDUCATION_OPTIONS[user?.education] || "-"}
              </strong>
            </div>
            <div className="profile-refresh-modal__summary-row">
              <span className="profile-refresh-modal__summary-label">
                <BookOpenIco /> Grade
              </span>
              <strong className="profile-refresh-modal__summary-value">
                {GRADE_OPTIONS[user?.grade] || "-"}
              </strong>
            </div>
            <div className="profile-refresh-modal__summary-row">
              <span className="profile-refresh-modal__summary-label">
                <User /> Age
              </span>
              <strong className="profile-refresh-modal__summary-value">{user?.age || "-"}</strong>
            </div>
          </div>
        )}
      </div>

      {saveError ? <p className="profile-refresh-modal__error">{saveError}</p> : null}

      <div className={`profile-refresh-modal__actions${isMonthly ? " is-monthly" : " is-six-month"}`}>
        {isMonthly ? (
          <button
            className="profile-refresh-modal__button profile-refresh-modal__button--secondary"
            onClick={onConfirm}
            type="button"
          >
            Looks good
          </button>
        ) : null}

        <button
          className="profile-refresh-modal__button profile-refresh-modal__button--primary"
          disabled={isSaving}
          onClick={onPrimaryAction}
          type="button"
        >
          {isMonthly ? (isSaving ? "Saving..." : "Save changes") : "Update my profile"}
        </button>
      </div>

      <button className="profile-refresh-modal__link" onClick={onClose} type="button">
        {isMonthly ? "Remind me later" : "Maybe later"}
      </button>
    </BaseModal>
  );
};

export default ProfileRefreshModal;
